import { c as _c } from "react/compiler-runtime";
import type { BadgeProps } from "@/components/ui/Badge";
import { Badge } from "@/components/ui/Badge";
import { Skeleton } from "@/components/ui/Skeleton";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import type { VariantProps } from "class-variance-authority";
import * as React from "react";
import cn from "twsx";
export type CardDivProps = React.HTMLAttributes<HTMLDivElement> & {
  ref?: React.Ref<HTMLDivElement>;
};

// Linear's settings panel: white surface, 1px border, 10px radius, no shadow.
// The border uses the popover hairline so the panel stays visible on the gray
// page canvas without a header band.
// - panel (default): the framed surface for a bounded module.
// - quiet: transparent, sits on the canvas, for row and action lists.
// - danger: a destructive zone (delete, revoke) framed in red.
// - overlay: a floating card (map popups) on the popover surface.
// - flush: no frame, for a card nested in a dialog or another surface.
const cardVariants = cva("overflow-hidden text-text-primary", {
  variants: {
    variant: {
      panel: "rounded-settings-list-view-item border border-border-popover bg-bg-surface",
      quiet: "rounded-settings-list-view-item border border-border-popover bg-transparent",
      danger: "rounded-settings-list-view-item border border-red-bg-subtle bg-bg-surface",
      overlay: "rounded-popover border border-border-popover bg-bg-surface [box-shadow:var(--elevation-pop)]",
      flush: "bg-bg-surface"
    },
    padding: {
      none: "",
      lg: "p-5"
    },
    height: {
      auto: "",
      fill: "h-full",
      tile: "h-full min-h-[180px]",
      viewport: "h-[calc(100vh-10rem)] min-h-[640px]"
    },
    interactive: {
      true: "hover:border-border-secondary hover:bg-fill-hover-subtle",
      false: ""
    }
  },
  defaultVariants: {
    variant: "panel",
    padding: "none",
    height: "auto",
    interactive: false
  }
});
export type CardProps = CardDivProps & VariantProps<typeof cardVariants>;

/** Use when grouping related content or settings on a bordered panel. */
const Card = t0 => {
  const $ = _c(18);
  let className;
  let height;
  let interactive;
  let padding;
  let props;
  let ref;
  let variant;
  if ($[0] !== t0) {
    ({
      className,
      variant,
      padding,
      height,
      interactive,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = height;
    $[3] = interactive;
    $[4] = padding;
    $[5] = props;
    $[6] = ref;
    $[7] = variant;
  } else {
    className = $[1];
    height = $[2];
    interactive = $[3];
    padding = $[4];
    props = $[5];
    ref = $[6];
    variant = $[7];
  }
  let t1;
  if ($[8] !== className || $[9] !== height || $[10] !== interactive || $[11] !== padding || $[12] !== variant) {
    t1 = cn(cardVariants({
      variant,
      padding,
      height,
      interactive
    }), className);
    $[8] = className;
    $[9] = height;
    $[10] = interactive;
    $[11] = padding;
    $[12] = variant;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== props || $[15] !== ref || $[16] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[14] = props;
    $[15] = ref;
    $[16] = t1;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
};
Card.displayName = "Card";
const CardSlot = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex grow flex-row items-center justify-between gap-4 px-4 py-3", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CardSlot.displayName = "CardSlot";

// Header row: 13/500 title text without a divider. `lg` is the taller
// header used when the title sits beside a description or controls;
// `justify="between"` pushes a trailing action to the right edge.
const cardHeaderVariants = cva(["flex flex-row items-center justify-start gap-x-2 px-4 text-base font-medium leading-5 text-text-primary", "[&_svg:not(button_svg)]:size-3.5 [&_svg:not(button_svg)]:shrink-0 [&_svg:not(button_svg)]:text-text-tertiary"], {
  variants: {
    size: {
      md: "min-h-11 py-2.5",
      lg: "min-h-14 gap-x-4 py-3"
    },
    justify: {
      start: "",
      between: "justify-between"
    }
  },
  defaultVariants: {
    size: "md",
    justify: "start"
  }
});
const CardHeader = t0 => {
  const $ = _c(14);
  let className;
  let justify;
  let props;
  let ref;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      justify,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = justify;
    $[3] = props;
    $[4] = ref;
    $[5] = size;
  } else {
    className = $[1];
    justify = $[2];
    props = $[3];
    ref = $[4];
    size = $[5];
  }
  let t1;
  if ($[6] !== className || $[7] !== justify || $[8] !== size) {
    t1 = cn(cardHeaderVariants({
      size,
      justify
    }), className);
    $[6] = className;
    $[7] = justify;
    $[8] = size;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== props || $[11] !== ref || $[12] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[10] = props;
    $[11] = ref;
    $[12] = t1;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  return t2;
};
CardHeader.displayName = "CardHeader";
const cardTitleVariants = cva("flex items-center gap-2 text-base font-medium leading-5", {
  variants: {
    tone: {
      default: "text-text-primary",
      danger: "text-red-text"
    }
  },
  defaultVariants: {
    tone: "default"
  }
});
const CardTitle = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let ref;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      tone,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = tone;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    tone = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== tone) {
    t1 = cn(cardTitleVariants({
      tone
    }), className);
    $[5] = className;
    $[6] = tone;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
CardTitle.displayName = "CardTitle";
const CardDescription = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("text-base leading-5 text-text-tertiary", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CardDescription.displayName = "CardDescription";

// Body: rows divide on a hairline by default; `divided={false}` stacks
// blocks (metric groups, sections) without dividers. `gap` spaces stacked
// blocks, `padding` sets the inset, and `tone` sets body copy colour.
const cardContentVariants = cva("flex flex-col items-start justify-start text-base leading-5", {
  variants: {
    divided: {
      true: "divide-y divide-border-subtle",
      false: ""
    },
    padding: {
      none: "p-0",
      sm: "p-2",
      md: "p-3",
      lg: "p-4",
      list: "p-2 [&>*]:px-2.5 [&>*]:py-2.5"
    },
    gap: {
      none: "",
      xs: "gap-1",
      md: "gap-3",
      lg: "gap-4"
    },
    tone: {
      default: "",
      secondary: "text-text-secondary",
      muted: "text-text-tertiary"
    }
  },
  defaultVariants: {
    divided: true,
    padding: "md",
    gap: "none",
    tone: "default"
  }
});
const CardContent = t0 => {
  const $ = _c(18);
  let className;
  let divided;
  let gap;
  let padding;
  let props;
  let ref;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      divided,
      padding,
      gap,
      tone,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = divided;
    $[3] = gap;
    $[4] = padding;
    $[5] = props;
    $[6] = ref;
    $[7] = tone;
  } else {
    className = $[1];
    divided = $[2];
    gap = $[3];
    padding = $[4];
    props = $[5];
    ref = $[6];
    tone = $[7];
  }
  let t1;
  if ($[8] !== className || $[9] !== divided || $[10] !== gap || $[11] !== padding || $[12] !== tone) {
    t1 = cn(cardContentVariants({
      divided,
      padding,
      gap,
      tone
    }), className);
    $[8] = className;
    $[9] = divided;
    $[10] = gap;
    $[11] = padding;
    $[12] = tone;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== props || $[15] !== ref || $[16] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[14] = props;
    $[15] = ref;
    $[16] = t1;
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  return t2;
};
CardContent.displayName = "CardContent";
const cardFooterVariants = cva("flex items-center justify-end gap-2 border-t border-border-primary px-3 py-2.5", {
  variants: {
    tone: {
      subtle: "bg-bg-primary",
      plain: "bg-bg-surface",
      muted: "bg-bg-primary text-xs text-text-tertiary"
    }
  },
  defaultVariants: {
    tone: "subtle"
  }
});
const CardFooter = t0 => {
  const $ = _c(12);
  let className;
  let props;
  let ref;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      tone,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = tone;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    tone = $[4];
  }
  let t1;
  if ($[5] !== className || $[6] !== tone) {
    t1 = cn(cardFooterVariants({
      tone
    }), className);
    $[5] = className;
    $[6] = tone;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== props || $[9] !== ref || $[10] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[8] = props;
    $[9] = ref;
    $[10] = t1;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  return t2;
};
CardFooter.displayName = "CardFooter";
const cardItemVariants = cva("flex w-full grow flex-1 flex-row flex-wrap items-center justify-between gap-x-2 gap-y-1 overflow-hidden", {
  variants: {
    size: {
      sm: "px-2 py-1.5",
      md: "min-h-10 px-3 py-2",
      lg: "min-h-14 px-4 py-3",
      flush: "p-0"
    },
    layout: {
      row: "",
      stack: "flex-col gap-y-2",
      split: "flex-nowrap",
      responsive: "gap-0 sm:flex-nowrap"
    },
    interactive: {
      true: "hover:bg-fill-hover-subtle focus-within:bg-fill-hover-subtle",
      false: ""
    }
  },
  defaultVariants: {
    size: "sm",
    layout: "row",
    interactive: false
  }
});
const CardItem = t0 => {
  const $ = _c(16);
  let className;
  let interactive;
  let layout;
  let props;
  let ref;
  let size;
  if ($[0] !== t0) {
    ({
      className,
      size,
      layout,
      interactive,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = interactive;
    $[3] = layout;
    $[4] = props;
    $[5] = ref;
    $[6] = size;
  } else {
    className = $[1];
    interactive = $[2];
    layout = $[3];
    props = $[4];
    ref = $[5];
    size = $[6];
  }
  let t1;
  if ($[7] !== className || $[8] !== interactive || $[9] !== layout || $[10] !== size) {
    t1 = cn(cardItemVariants({
      size,
      layout,
      interactive
    }), className);
    $[7] = className;
    $[8] = interactive;
    $[9] = layout;
    $[10] = size;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== props || $[13] !== ref || $[14] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[12] = props;
    $[13] = ref;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
};
CardItem.displayName = "CardItem";

// A row that opens something: the whole row is the button (or, with
// `asChild`, the link). Inside a `CardItem size="flush" layout="responsive"`
// it fills the row beside `CardItemActions`. `tone="muted"` is the quiet
// 12px disclosure row, such as "Show all" under a metric list.
const CardItemButton = t0 => {
  const $ = _c(18);
  let className;
  let props;
  let ref;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      asChild: t1,
      className,
      size: t2,
      type: t3,
      tone: t4,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const asChild = t1 === undefined ? false : t1;
  const size = t2 === undefined ? "md" : t2;
  const type = t3 === undefined ? "button" : t3;
  const tone = t4 === undefined ? "default" : t4;
  const Comp = asChild ? Slot : "button";
  const t5 = asChild ? undefined : type;
  let t6;
  if ($[8] !== className || $[9] !== size || $[10] !== tone) {
    t6 = cn(cardItemVariants({
      size,
      layout: "split"
    }), "cursor-pointer text-left outline-none hover:bg-fill-hover-subtle", "focus-visible:outline-1 focus-visible:-outline-offset-2 focus-visible:outline-accent", tone === "muted" && "justify-start gap-x-1 text-xs text-text-tertiary hover:text-text-primary", className);
    $[8] = className;
    $[9] = size;
    $[10] = tone;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== Comp || $[13] !== props || $[14] !== ref || $[15] !== t5 || $[16] !== t6) {
    t7 = <Comp ref={ref} type={t5} className={t6} {...props} />;
    $[12] = Comp;
    $[13] = props;
    $[14] = ref;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
CardItemButton.displayName = "CardItemButton";

// Trailing row actions: a two-column grid under the row on small screens,
// inline at the row's end from `sm`.
const CardItemActions = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("grid w-full grid-cols-2 items-center gap-2 px-4 pb-3 sm:ml-auto sm:flex sm:w-auto sm:shrink-0 sm:py-3 sm:pl-2 sm:pr-4", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <div ref={ref} className={t1} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
CardItemActions.displayName = "CardItemActions";
const cardItemLabelVariants = cva("flex shrink-0 items-center gap-1.5 text-base", {
  variants: {
    tone: {
      muted: "text-text-tertiary",
      default: "text-text-primary",
      faint: "text-text-quaternary"
    }
  },
  defaultVariants: {
    tone: "muted"
  }
});
const CardItemLabel = t0 => {
  const $ = _c(18);
  let children;
  let className;
  let icon;
  let props;
  let ref;
  let tone;
  if ($[0] !== t0) {
    ({
      className,
      icon,
      tone,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = icon;
    $[4] = props;
    $[5] = ref;
    $[6] = tone;
  } else {
    children = $[1];
    className = $[2];
    icon = $[3];
    props = $[4];
    ref = $[5];
    tone = $[6];
  }
  let t1;
  if ($[7] !== className || $[8] !== tone) {
    t1 = cn(cardItemLabelVariants({
      tone
    }), className);
    $[7] = className;
    $[8] = tone;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== icon) {
    t2 = icon && <span className="flex shrink-0 items-center text-text-quaternary [&>svg]:size-3.5">{icon}</span>;
    $[10] = icon;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== children || $[13] !== props || $[14] !== ref || $[15] !== t1 || $[16] !== t2) {
    t3 = <div ref={ref} className={t1} {...props}>{t2}{children}</div>;
    $[12] = children;
    $[13] = props;
    $[14] = ref;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
};
CardItemLabel.displayName = "CardItemLabel";

// `layout: "inline"` wraps several values (badges, links) on one line.
const CardItemValue = t0 => {
  const $ = _c(27);
  let children;
  let className;
  let href;
  let layout;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      href,
      layout,
      children,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = href;
    $[4] = layout;
    $[5] = props;
    $[6] = ref;
  } else {
    children = $[1];
    className = $[2];
    href = $[3];
    layout = $[4];
    props = $[5];
    ref = $[6];
  }
  if (children === null || children === undefined) {
    let t1;
    if ($[7] !== className) {
      t1 = cn("min-h-5 text-base text-text-tertiary", className);
      $[7] = className;
      $[8] = t1;
    } else {
      t1 = $[8];
    }
    let t2;
    if ($[9] !== props || $[10] !== ref || $[11] !== t1) {
      t2 = <div ref={ref} className={t1} {...props}>-</div>;
      $[9] = props;
      $[10] = ref;
      $[11] = t1;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    return t2;
  }
  if (href) {
    let t1;
    if ($[13] !== className) {
      t1 = cn("text-base text-text-link hover:text-primary-800 hover:underline", className);
      $[13] = className;
      $[14] = t1;
    } else {
      t1 = $[14];
    }
    let t2;
    if ($[15] !== children || $[16] !== href || $[17] !== t1) {
      t2 = <a href={href} target="_blank" rel="noreferrer" className={t1}>{children}</a>;
      $[15] = children;
      $[16] = href;
      $[17] = t1;
      $[18] = t2;
    } else {
      t2 = $[18];
    }
    return t2;
  }
  const t1 = layout === "inline" && "flex flex-wrap items-center gap-1.5";
  let t2;
  if ($[19] !== className || $[20] !== t1) {
    t2 = cn("min-h-5 text-base font-medium leading-5", t1, className);
    $[19] = className;
    $[20] = t1;
    $[21] = t2;
  } else {
    t2 = $[21];
  }
  let t3;
  if ($[22] !== children || $[23] !== props || $[24] !== ref || $[25] !== t2) {
    t3 = <div ref={ref} className={t2} {...props}>{children}</div>;
    $[22] = children;
    $[23] = props;
    $[24] = ref;
    $[25] = t2;
    $[26] = t3;
  } else {
    t3 = $[26];
  }
  return t3;
};
CardItemValue.displayName = "CardItemValue";
const CardItemSkeleton = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <CardItemLabel><Skeleton className="w-20" /></CardItemLabel>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CardItem>{t0}<CardItemValue><Skeleton className="w-36" /></CardItemValue></CardItem>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
CardItemSkeleton.displayName = "CardItemSkeleton";

// A yes/no value rendered as a Badge, for boolean card rows.
const CardItemBoolean = t0 => {
  const $ = _c(16);
  let className;
  let props;
  let ref;
  let t1;
  let value;
  if ($[0] !== t0) {
    ({
      className,
      falseColor: t1,
      value,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
    $[4] = t1;
    $[5] = value;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
    t1 = $[4];
    value = $[5];
  }
  const falseColor = t1 === undefined ? "neutral" : t1;
  let t2;
  if ($[6] !== className) {
    t2 = cn("shrink-0 text-base", className);
    $[6] = className;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== falseColor || $[9] !== value) {
    t3 = value ? <Badge color="success">Yes</Badge> : <Badge color={falseColor}>No</Badge>;
    $[8] = falseColor;
    $[9] = value;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== props || $[12] !== ref || $[13] !== t2 || $[14] !== t3) {
    t4 = <div ref={ref} className={t2} {...props}>{t3}</div>;
    $[11] = props;
    $[12] = ref;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
};
CardItemBoolean.displayName = "CardItemBoolean";
export { Card, CardContent, CardDescription, CardFooter, CardHeader, CardItem, CardItemActions, CardItemBoolean, CardItemButton, CardItemLabel, CardItemSkeleton, CardItemValue, CardSlot, CardTitle };